import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { toast } from "@/components/ui/toast"
import {
  getMemberRsvpSummary,
  getRoleLabel,
  getRsvpLabel,
} from "@/core/guild-events/labels"
import {
  type GuildEventRsvpState,
  type WwmEventRoleKey,
} from "@/core/guild-events/types"
import {
  GuildEventsApiError,
  type GuildEventRecord,
  type GuildEventSignupRecord,
  guildEventsApi,
} from "@/lib/guild-events-api"
import { guildEventsKeys } from "@/lib/query-keys"

const RSVP_STATES: GuildEventRsvpState[] = [
  "attending",
  "tentative",
  "not_attending",
]

export function MemberRsvpControl({
  event,
  mySignup,
}: {
  event: GuildEventRecord
  mySignup: GuildEventSignupRecord | undefined
}) {
  const queryClient = useQueryClient()
  const hasRoles = event.eventKind === "raid"
  const [selectedRole, setSelectedRole] = useState<WwmEventRoleKey | undefined>(
    mySignup?.roleKey ?? undefined,
  )
  const [closedByError, setClosedByError] = useState(false)

  if (!event.rsvpEnabled) {
    return (
      <Card>
        <CardContent className="pt-5 text-sm text-muted-foreground">
          RSVPs are not enabled for this event.
        </CardContent>
      </Card>
    )
  }

  const rsvpMutation = useMutation({
    mutationFn: (input: {
      state: GuildEventRsvpState
      roleKey: WwmEventRoleKey | null
    }) => guildEventsApi.setMemberRsvp(event.id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: guildEventsKeys.all })
      toast.success("RSVP updated")
    },
    onError: (error: Error) => {
      if (
        error instanceof GuildEventsApiError &&
        error.code === "EVENT_SIGNUPS_CLOSED"
      ) {
        setClosedByError(true)
        void queryClient.invalidateQueries({ queryKey: guildEventsKeys.all })
        return
      }
      toast.error(error.message)
    },
  })

  const submit = (
    state: GuildEventRsvpState,
    roleKey = state === "attending" && hasRoles ? (selectedRole ?? null) : null,
  ) => {
    rsvpMutation.mutate({ state, roleKey })
  }

  if (closedByError) {
    return (
      <Card>
        <CardContent className="grid gap-3 pt-5">
          <div className="text-sm text-muted-foreground">
            RSVP changes are no longer available for this event.
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent className="grid gap-3 pt-5">
        <div className="text-sm font-semibold">Your RSVP</div>
        <div className="text-sm text-muted-foreground">
          {mySignup
            ? getMemberRsvpSummary(mySignup.state, mySignup.roleKey)
            : "You haven't RSVP'd yet."}
        </div>
        {hasRoles ? (
          <>
            <div className="text-xs text-muted-foreground">
              {mySignup?.state === "attending"
                ? "Choose a role to update your attending RSVP."
                : "Choose a role to RSVP as attending."}
            </div>
            <div className="grid gap-2">
              <label
                htmlFor="rsvp-role"
                className="text-xs font-semibold tracking-wide text-muted-foreground uppercase"
              >
                Role
              </label>
              <Select
                value={selectedRole}
                onValueChange={(next) => {
                  if (!next) return
                  const role = next as WwmEventRoleKey
                  setSelectedRole(role)
                  submit("attending", role)
                }}
              >
                <SelectTrigger
                  id="rsvp-role"
                  className="w-full"
                  disabled={rsvpMutation.isPending}
                >
                  <SelectValue placeholder="Choose a role" />
                </SelectTrigger>
                <SelectContent>
                  {event.eventRoles.map((role) => (
                    <SelectItem key={role} value={role}>
                      {getRoleLabel(role)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        ) : null}
        <div className="flex flex-wrap gap-2">
          {RSVP_STATES.filter(
            (state) => !hasRoles || state !== "attending",
          ).map((state) => (
            <Button
              key={state}
              type="button"
              size="sm"
              variant={mySignup?.state === state ? "default" : "outline"}
              disabled={rsvpMutation.isPending}
              onClick={() => submit(state)}
            >
              {getRsvpLabel(state)}
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
